import type { StringifiedParams } from '@kizen/api';
import type { BulkActionSummaryInitializeRequest } from '@kizen/api/bulk-actions';
import type {
  CustomerNotificatonBulkAction,
  CustomerNotificationParams,
} from '@kizen/api/customer-notifications';

import styled from '@emotion/styled';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import Button from '__components/Button';
import KizenLoader from '__components/Kizen/Loader';
import Select from '__components/Inputs/Select';
import BasicModal from '__components/Modals/presets/BasicModal';
import BasicModalWithConfirmation from '__components/Modals/presets/BasicModalWithConfirmation';
import { ButtonGroupImageButton } from 'components/Inputs/ButtonGroup/Button';
import {
  bulkActionSummaryQuery,
  bulkActionSummaryInitializeQuery,
} from 'query/bulk-actions';
import { POLLING_TIMEOUTS } from './model';
import {
  BulkActionInfoWrapper,
  BulkActionTextWrapper,
} from './StepsContent/styles';
import { StyledModalBody, StyledModalFooter } from './styles';
import TypeNotificationRead from './assets/type-notification-read.svg?react';
import TypeNotificationUnread from './assets/type-notification-unread.svg?react';
import TypeNotificationArchive from './assets/type-notification-archive.svg?react';
import TypeNotificationUnarchive from './assets/type-notification-unarchive.svg?react';

type Action = CustomerNotificatonBulkAction['action'];

type ActionGroup = 'all' | 'none' | 'search';

type BulkActionOption = {
  label: string;
  value: Action;
};

type ActionGroupOption = {
  label: string;
  value: ActionGroup;
};

type BulkActionNotificationModalProps = {
  action: Action | null;
  params: CustomerNotificationParams;
  group: ActionGroup;
  ids: Set<string>;
  search: string | null;
  onComplete(data: { count: number; key: string }): void;
  onGoBack(): void;
  onHide(): void;
  onSelectAction(action: Action): void;
  onSelectGroup(group: ActionGroup): void;
};

export const useBulkNotificationActionOptions = () => {
  const { t } = useTranslation();

  return useMemo<BulkActionOption[]>(() => {
    return [
      { value: 'notifications_mark_as_read', label: t('Mark as Read') },
      { value: 'notifications_mark_as_unread', label: t('Mark as Unread') },
      { value: 'notifications_archive', label: t('Archive') },
      { value: 'notifications_unarchive', label: t('Unarchive') },
    ];
  }, [t]);
};

const Loader = styled(KizenLoader)`
  width: 100%;
  text-align: center;
  padding: 10px !important;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
`;

const getNotificationActionIcon = (value: Action) => {
  switch (value) {
    case 'notifications_mark_as_read':
      return <TypeNotificationRead />;
    case 'notifications_mark_as_unread':
      return <TypeNotificationUnread />;
    case 'notifications_archive':
      return <TypeNotificationArchive />;
    case 'notifications_unarchive':
      return <TypeNotificationUnarchive />;
  }
};

const getInitializeRequest = (
  action: Action,
  options: Pick<
    BulkActionNotificationModalProps,
    'group' | 'params' | 'ids' | 'search'
  >
): BulkActionSummaryInitializeRequest => {
  const { group, ids, params, search } = options;

  if (group === 'all') {
    return { action };
  }

  if (group === 'none') {
    if (ids.size) {
      return { action, record_ids: Array.from(ids) };
    }

    return { action };
  }

  const { archived, date_from, date_to, notification_type, read, scope, status } =
    params;

  const filters: StringifiedParams<CustomerNotificationParams> = {
    archived,
    date_from,
    date_to,
    read,
    scope,
  };

  if (notification_type) {
    filters.notification_type = notification_type.join(',');
  }

  if (status) {
    filters.status = status.join(',');
  }

  if (search) {
    return { action, filters, search_text: search };
  }

  return { action, filters };
};

export const BulkNotificationActionModal = (
  props: BulkActionNotificationModalProps
) => {
  const {
    action = null,
    params,
    group: groupProp = 'none',
    ids,
    search,
    onComplete,
    onGoBack,
    onHide,
    onSelectAction,
    onSelectGroup,
  } = props;

  const { t } = useTranslation();
  const qc = useQueryClient();
  const [group, setGroup] = useState<ActionGroup>(groupProp);
  const [pendingAction, setPendingAction] = useState<Action | null>(null);
  const options = useBulkNotificationActionOptions();
  const summaryFetchCount = useRef(-1);

  const selectGroupRequired = groupProp === 'none' && !ids.size;

  const initializeQueryOptions = useMemo(() => {
    summaryFetchCount.current = -1;

    const body = getInitializeRequest(action!, {
      group,
      ids,
      params,
      search,
    });

    return bulkActionSummaryInitializeQuery({
      body,
      enabled: Boolean(action),
    });
  }, [action, ids, group, params, search]);

  const initializeQuery = useQuery(initializeQueryOptions);

  const summaryId = initializeQuery.data?.summary_id ?? '';
  const enabled = Boolean(summaryId);

  const summaryQuery = useQuery(
    bulkActionSummaryQuery(summaryId, {
      enabled,
      staleTime: Infinity,
      refetchInterval: (q) => {
        if (
          !enabled ||
          q.state.data?.status === 'completed' ||
          q.state.data?.status === 'failed'
        ) {
          return false;
        }

        return POLLING_TIMEOUTS[summaryFetchCount.current] || 10_000;
      },
    })
  );

  useEffect(() => {
    if (initializeQuery.isFetching) {
      summaryFetchCount.current += 1;
    }
  }, [initializeQuery.isFetching]);

  if (selectGroupRequired) {
    const groupOptions: ActionGroupOption[] = [
      { value: 'search', label: t('All in Search') },
      { value: 'all', label: t('All Notifications') },
    ];

    return (
      <BasicModal
        show
        size="medium"
        buttonText={t('Next')}
        leftBtn={null}
        disabled={group === 'none'}
        heading={t(
          'Which Notifications Would You Like to Perform an Action On?'
        )}
        onHide={onHide}
        onConfirm={() => {
          onSelectGroup(group);
        }}
      >
        <div style={{ height: '190px' }}>
          <Select
            label={t('Choose Action Group')}
            value={group}
            placeholder={t('Choose Group')}
            onChange={(option: ActionGroupOption) => setGroup(option.value)}
            options={groupOptions}
          />
        </div>
      </BasicModal>
    );
  }

  const isActionSelected = Boolean(action);
  const { cache_key, stats, status } = summaryQuery.data ?? {};
  const { allowed_count } = stats ?? {};
  const showLoader =
    !status || status === 'initialize' || status === 'in_progress';
  const amount = group === 'all' ? t('All') : t('Selected');

  let title = t('Perform Action on {{amount}} Notifications', { amount });

  if (isActionSelected) {
    if (action === 'notifications_archive') {
      title = t('Archive {{amount}} Notifications', { amount });
    } else if (action === 'notifications_unarchive') {
      title = t('Unarchive {{amount}} Notifications', { amount });
    } else if (action === 'notifications_mark_as_read') {
      title = t('Mark {{amount}} Notifications as Read', { amount });
    } else {
      title = t('Mark {{amount}} Notifications as Unread', { amount });
    }
  }

  return (
    <BasicModalWithConfirmation
      show
      hideFooter
      size="small"
      enforceFocus={false}
      heading={title}
      onHide={onHide}
    >
      <StyledModalBody>
        {!isActionSelected && (
          <ButtonGrid>
            {options.map((option) => (
              <ButtonGroupImageButton
                key={option.value}
                selected={pendingAction === option.value}
                disabled={false}
                fieldId={undefined}
                onClick={() => setPendingAction(option.value)}
                option={{
                  label: option.label,
                  image: getNotificationActionIcon(option.value),
                }}
                data-qa-id={`perform-action-${option.value}`}
              />
            ))}
          </ButtonGrid>
        )}
        {isActionSelected && showLoader && <Loader loading />}
        {isActionSelected && !showLoader && (
          <BulkActionInfoWrapper marginBottom>
            <BulkActionTextWrapper>
              <KizenTypography as="span">
                {t('This action will be performed on')}&nbsp;
              </KizenTypography>
              <KizenTypography
                weight="bold"
                as="span"
                data-qa="total"
                textTransform="lowercase"
              >
                {`${allowed_count} ${allowed_count === 1 ? t('Notification') : t('Notifications')}`}
              </KizenTypography>
            </BulkActionTextWrapper>
          </BulkActionInfoWrapper>
        )}
      </StyledModalBody>
      <StyledModalFooter>
        {!isActionSelected && (
          <div className="button-box">
            <Button variant="text" color="blue" onClick={onHide}>
              {t('Cancel')}
            </Button>
            <Button
              disabled={!pendingAction}
              onClick={() => {
                if (pendingAction) onSelectAction(pendingAction);
              }}
            >
              {t('Next')}
            </Button>
          </div>
        )}
        {isActionSelected && (
          <div className="button-box">
            <Button
              variant="text"
              color="blue"
              onClick={() => {
                qc.removeQueries({
                  queryKey: initializeQueryOptions.queryKey,
                });

                onGoBack();
              }}
            >
              {t('Back')}
            </Button>
            <Button
              disabled={showLoader}
              onClick={() => {
                setPendingAction(null);
                if (cache_key) {
                  onComplete({ count: allowed_count ?? 0, key: cache_key });
                }
              }}
            >
              {t('Next')}
            </Button>
          </div>
        )}
      </StyledModalFooter>
    </BasicModalWithConfirmation>
  );
};
